import { Icon, Icons } from 'components/Icon';
import React, { useEffect, useState } from 'react';
import TextField from 'components/Form/TextField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import Spacer from 'components/Spacer';
import { useCreateTrustee } from 'common/queries-and-mutations/trustee';
import { BeneficiaryTypeEnum } from 'apps/user/types/user.enum.type';
import TextAreaFieldWithAutoHeightResize from 'components/Form/TextAreaFieldWithAutoHeightResize';
import { TrusteesType } from 'apps/user/types/on-boarding.type';
import { services } from 'service';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import { InfoModal } from 'components/Modal';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
};

export default function AddTrusteesModal({ onClose, onSuccess }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isLoading, isError } = useCreateTrustee();

  const formik = useFormik({
    initialValues: {
      trustees: [
        {
          trusteeId: null,
          trusteeFirstName: '',
          trusteeMiddleName: '',
          trusteeLastName: '',
          trusteeType: '',
          address: '',
          emailAddress: '',
          phoneNumber: '',
          dutiesInstruction: '',
          rcNumber: '',
          incorporationDate: '',
          incorporationPlace: ''
        }
      ]
    },
    validationSchema: Yup.object({
      trustees: Yup.array().of(
        Yup.object().shape({
          trusteeFirstName: Yup.string().when('trusteeType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string()
              .required('Trustee first name is required')
              .nullable(),
            otherwise: Yup.string().required('Name is required').nullable()
          }),
          trusteeLastName: Yup.string().when('trusteeType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string()
              .required('Trustee last name is required')
              .nullable(),
            otherwise: Yup.string()
          }),
          trusteeType: Yup.string()
            .required('Trustee type is required')
            .nullable(),
          address: Yup.string().required('Address is required').nullable(),
          emailAddress: Yup.string()
            .email('Invalid email')
            .required('Email is required')
            .nullable()
            .test('is-allowed', 'Email already in use', async function (value) {
              if (value) {
                const isInUse =
                  await services.trusteeService.checkIfEmailInUse(value);
                return !isInUse;
              }
              return true;
            }),
          phoneNumber: Yup.string().when('trusteeType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string().required('Phone number is required').nullable(),
            otherwise: Yup.string()
          }),
          dutiesInstruction: Yup.string().when('trusteeType', {
            is: BeneficiaryTypeEnum.CORPORATE,
            then: Yup.string()
              .required('Duties and Instructions is required')
              .nullable(),
            otherwise: Yup.string()
          }),
          rcNumber: Yup.string().when('trusteeType', {
            is: BeneficiaryTypeEnum.CORPORATE,
            then: Yup.string().required('RC Number is required').nullable(),
            otherwise: Yup.string()
          })
        })
      )
    }),
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onTrusteeSubmit(values);
    }
  });

  const onTrusteeSubmit = (formValue: TrusteesType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      toast(<Alert message="Trustee Successfully Added" type="success" />);
      setIsSubmitting(false);
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <InfoModal
      width="w-[55%]"
      className="h-fit rounded-md px-4 pb-9 pt-5 sm:p-9"
    >
      <div>
        <div className="flex flex-row items-center justify-between pl-6">
          <p className="text-2xl font-bold text-[#353535]">Add Trustee</p>
          <button type="button" onClick={onClose}>
            <Icon name={Icons.Close} />
          </button>
        </div>
        <form
          autoComplete="false"
          className="mt-10 px-6"
          onSubmit={formik.handleSubmit}
        >
          {formik.values.trustees.map((trustee, index) => (
            <div key={index} className="mb-1 mt-8">
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`trustees[${index}].trusteeFirstName`}
                    placeholder=""
                    value={trustee.trusteeFirstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label={
                      trustee.trusteeType === BeneficiaryTypeEnum.INDIVIDUAL
                        ? 'Trustee First Name'
                        : 'Name'
                    }
                    error={
                      formik.touched.trustees
                        ? formik.errors.trustees?.[index] &&
                          formik.errors.trustees.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.trustees[index])
                            ).trusteeFirstName
                          : ''
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <div className="relative mb-2 flex items-center justify-between">
                    <span className="flex items-center">
                      <label className="text-[12px] font-medium text-[#353535]">
                        Type of Trustee
                      </label>
                      <div className="mx-2 mb-2">
                        <Spacer width={3} />
                        <Icon name={Icons.Required} />
                      </div>
                    </span>
                  </div>
                  <div className="mt-5 flex items-center gap-10">
                    <button
                      type="button"
                      onClick={() =>
                        formik.setFieldValue(
                          `trustees[${index}].trusteeType`,
                          BeneficiaryTypeEnum.INDIVIDUAL
                        )
                      }
                      className="flex items-center gap-3"
                    >
                      <span className="text-base font-bold text-[#353535]">
                        Individual
                      </span>
                      <Icon
                        name={
                          trustee.trusteeType === BeneficiaryTypeEnum.INDIVIDUAL
                            ? Icons.CheckedBox
                            : Icons.UncheckBox
                        }
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formik.setFieldValue(
                          `trustees[${index}].trusteeType`,
                          BeneficiaryTypeEnum.CORPORATE
                        )
                      }
                      className="flex items-center gap-3"
                    >
                      <span className="text-base font-bold text-[#353535]">
                        Corporate
                      </span>
                      <Icon
                        name={
                          trustee.trusteeType === BeneficiaryTypeEnum.CORPORATE
                            ? Icons.CheckedBox
                            : Icons.UncheckBox
                        }
                      />
                    </button>
                  </div>
                </div>
              </div>
              {trustee.trusteeType === BeneficiaryTypeEnum.INDIVIDUAL && (
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`trustees[${index}].trusteeMiddleName`}
                      placeholder=""
                      value={trustee.trusteeMiddleName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Trustee Middle Name"
                      error={
                        formik.touched.trustees
                          ? formik.errors.trustees?.[index] &&
                            formik.errors.trustees.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.trustees[index])
                              ).trusteeMiddleName
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`trustees[${index}].trusteeLastName`}
                      placeholder=""
                      value={trustee.trusteeLastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Trustee Last Name"
                      error={
                        formik.touched.trustees
                          ? formik.errors.trustees?.[index] &&
                            formik.errors.trustees.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.trustees[index])
                              ).trusteeLastName
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
              )}
              {trustee.trusteeType === BeneficiaryTypeEnum.INDIVIDUAL && (
                <div>
                  <div className="grid md:grid-cols-2 md:gap-10">
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`trustees[${index}].address`}
                        placeholder=""
                        isRequired
                        value={trustee.address}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Home Address"
                        error={
                          formik.touched.trustees
                            ? formik.errors.trustees?.[index] &&
                              formik.errors.trustees.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.trustees[index])
                                ).address
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`trustees[${index}].emailAddress`}
                        placeholder=""
                        value={trustee.emailAddress}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Email Address"
                        isRequired
                        error={
                          formik.touched.trustees
                            ? formik.errors.trustees?.[index] &&
                              formik.errors.trustees.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.trustees[index])
                                ).emailAddress
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 md:gap-10">
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`trustees[${index}].phoneNumber`}
                        placeholder=""
                        isRequired
                        value={trustee.phoneNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Phone Number"
                        error={
                          formik.touched.trustees
                            ? formik.errors.trustees?.[index] &&
                              formik.errors.trustees.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.trustees[index])
                                ).phoneNumber
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                    <div className="group relative z-0 mb-6 w-full">
                      <TextAreaFieldWithAutoHeightResize
                        label="Duties and Instructions"
                        isRequired
                        name={`trustees[${index}].dutiesInstruction`}
                        value={trustee.dutiesInstruction}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder=""
                        rows={6}
                        error={
                          formik.touched.trustees
                            ? formik.errors.trustees?.[index] &&
                              formik.errors.trustees.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.trustees[index])
                                ).dutiesInstruction
                              : ''
                            : ''
                        }
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                  </div>
                </div>
              )}
              {trustee.trusteeType === BeneficiaryTypeEnum.CORPORATE && (
                <div>
                  <div className="grid md:grid-cols-2 md:gap-10">
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`trustees[${index}].rcNumber`}
                        placeholder=""
                        value={trustee.rcNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="RC Number"
                        isRequired
                        error={
                          formik.touched.trustees
                            ? formik.errors.trustees?.[index] &&
                              formik.errors.trustees.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.trustees[index])
                                ).rcNumber
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`trustees[${index}].address`}
                        placeholder=""
                        isRequired
                        value={trustee.address}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Company Address"
                        error={
                          formik.touched.trustees
                            ? formik.errors.trustees?.[index] &&
                              formik.errors.trustees.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.trustees[index])
                                ).address
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 md:gap-10">
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`trustees[${index}].emailAddress`}
                        placeholder=""
                        value={trustee.emailAddress}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Email Address"
                        isRequired
                        error={
                          formik.touched.trustees
                            ? formik.errors.trustees?.[index] &&
                              formik.errors.trustees.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.trustees[index])
                                ).emailAddress
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`trustees[${index}].incorporationDate`}
                        placeholder=""
                        value={trustee.incorporationDate}
                        validateDate={false}
                        type="date"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Date of Incorporation"
                        error={
                          formik.touched.trustees
                            ? formik.errors.trustees?.[index] &&
                              formik.errors.trustees.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.trustees[index])
                                ).incorporationDate
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 md:gap-10">
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`trustees[${index}].incorporationPlace`}
                        placeholder=""
                        value={trustee.incorporationPlace}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Place of Incorporation"
                        error={
                          formik.touched.trustees
                            ? formik.errors.trustees?.[index] &&
                              formik.errors.trustees.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.trustees[index])
                                ).incorporationPlace
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                    <div className="group relative z-0 mb-6 w-full">
                      <TextAreaFieldWithAutoHeightResize
                        label="Duties and Instructions"
                        isRequired
                        name={`trustees[${index}].dutiesInstruction`}
                        value={trustee.dutiesInstruction}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder=""
                        rows={6}
                        error={
                          formik.touched.trustees
                            ? formik.errors.trustees?.[index] &&
                              formik.errors.trustees.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.trustees[index])
                                ).dutiesInstruction
                              : ''
                            : ''
                        }
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          <div className="flex items-center justify-between gap-2">
            <div className="flex w-full items-center gap-2">
              <Icon name={Icons.Required} />
              <span className="text-[10px] text-[#7A7C87]">
                Indicates Required
              </span>
            </div>
          </div>

          <div className="mt-5 flex items-end justify-end gap-8">
            <Button
              variant="primary"
              size="md"
              className="mt-5 w-[9rem] rounded p-[0.1rem]"
              label="Add Trustee"
              type="submit"
              disabled={!formik.isValid || isSubmitting}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </form>
      </div>
    </InfoModal>
  );
}
